import { useState, useRef, useCallback, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { 
  UploadCloud, 
  FileDown, 
  Cpu, 
  AlertTriangle, 
  X,
  Loader2,
  FileText,
  Share2,
  Check,
  ArrowLeft,
  Activity,
  Zap,
  Shield,
  AlertCircle,
  Server,
  Database,
  Cloud,
  Target,
  TrendingDown,
  ChevronRight,
  AlertOctagon,
  CheckCircle2,
  Clock,
  Lock,
  Workflow,
  ArrowRight,
  Gauge,
  MessageSquarePlus,
  Trash2,
  Save,
  User,
  Mail,
  FlaskConical,
  ExternalLink,
  Linkedin,
  Globe,
  Beaker,
  GitBranch,
  Info
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Risk Status Badge
const RiskBadge = ({ status }) => {
  const config = {
    critical: { bg: "bg-red-500", text: "text-white", icon: AlertOctagon, label: "CRITICAL" },
    warning: { bg: "bg-yellow-500", text: "text-black", icon: AlertTriangle, label: "WARNING" },
    healthy: { bg: "bg-green-500", text: "text-white", icon: CheckCircle2, label: "HEALTHY" },
    high: { bg: "bg-red-500", text: "text-white", icon: AlertOctagon, label: "HIGH" },
    medium: { bg: "bg-yellow-500", text: "text-black", icon: AlertTriangle, label: "MEDIUM" },
    low: { bg: "bg-green-500", text: "text-white", icon: CheckCircle2, label: "LOW" }
  };
  const c = config[status] || config.medium;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${c.bg} ${c.text}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
};

// Grade Display
const GradeDisplay = ({ grade, size = "normal" }) => {
  const colors = {
    A: "bg-green-500 text-white",
    B: "bg-blue-500 text-white",
    C: "bg-yellow-500 text-black",
    D: "bg-orange-500 text-white",
    F: "bg-red-500 text-white"
  };
  const sizeClasses = size === "large" ? "w-20 h-20 text-4xl" : "w-12 h-12 text-2xl";
  return (
    <div className={`${sizeClasses} flex items-center justify-center font-black ${colors[grade] || colors.C}`}>
      {grade}
    </div>
  );
};

// Score Bar
const ScoreBar = ({ score, label, showValue = true }) => {
  const getColor = (s) => {
    if (s >= 80) return "bg-green-500";
    if (s >= 60) return "bg-blue-500";
    if (s >= 40) return "bg-yellow-500";
    return "bg-red-500";
  };
  return (
    <div className="w-full">
      {label && <div className="flex justify-between text-xs mb-1">
        <span className="text-[#4B5563] uppercase tracking-wider">{label}</span>
        {showValue && <span className="font-bold">{score}/100</span>}
      </div>}
      <div className="h-2 bg-[#E5E7EB] w-full">
        <div className={`h-full ${getColor(score)} transition-all duration-500`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
};

// Annotation Component
const DiagramAnnotator = ({ imagePreview, annotations, setAnnotations, readOnly = false }) => {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState({ x: 0, y: 0, text: "" });
  const containerRef = useRef(null);

  const handleImageClick = (e) => {
    if (readOnly || !isAddingNote) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setNewNote({ x, y, text: "" });
  };

  const saveAnnotation = () => {
    if (newNote.text.trim()) {
      setAnnotations([...annotations, { ...newNote, id: Date.now() }]);
      setNewNote({ x: 0, y: 0, text: "" });
      setIsAddingNote(false);
      toast.success("Annotation added!");
    }
  };

  const deleteAnnotation = (id) => {
    setAnnotations(annotations.filter(a => a.id !== id));
    toast.success("Annotation removed");
  };

  return (
    <div className="relative">
      {/* Toolbar */}
      {!readOnly && (
        <div className="flex items-center gap-2 p-2 border-b border-[#E5E5E5] bg-[#F9FAFB]">
          <Button
            variant={isAddingNote ? "default" : "outline"}
            size="sm"
            onClick={() => setIsAddingNote(!isAddingNote)}
            className="rounded-none text-xs"
            data-testid="add-annotation-btn"
          >
            <MessageSquarePlus className="w-3 h-3 mr-1" />
            {isAddingNote ? "Click on diagram" : "Add Note"}
          </Button>
          {annotations.length > 0 && (
            <span className="text-xs text-[#4B5563]">{annotations.length} annotation(s)</span>
          )}
        </div>
      )}

      {/* Image Container */}
      <div
        ref={containerRef}
        className={`relative min-h-[300px] flex items-center justify-center bg-[#F9FAFB] ${isAddingNote ? 'cursor-crosshair' : ''}`}
        onClick={handleImageClick}
      >
        {imagePreview ? (
          <img src={imagePreview} alt="Architecture" className="max-w-full max-h-[400px] object-contain p-4" />
        ) : (
          <Cpu className="w-12 h-12 text-[#E5E7EB]" />
        )}

        {/* Annotation Markers */}
        {annotations.map((ann, idx) => (
          <div
            key={ann.id}
            className="absolute group"
            style={{ left: `${ann.x}%`, top: `${ann.y}%`, transform: 'translate(-50%, -50%)' }}
          >
            <div className="w-6 h-6 bg-[#002FA7] text-white rounded-full flex items-center justify-center text-xs font-bold cursor-pointer hover:bg-[#001d6e] transition-colors">
              {idx + 1}
            </div>
            <div className="absolute left-8 top-0 bg-white border border-[#E5E5E5] shadow-lg p-2 min-w-[200px] opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <p className="text-xs text-[#0A0A0A]">{ann.text}</p>
              {!readOnly && (
                <button
                  onClick={(e) => { e.stopPropagation(); deleteAnnotation(ann.id); }}
                  className="mt-2 text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              )}
            </div>
          </div>
        ))}

        {/* New Annotation Input */}
        {isAddingNote && newNote.x > 0 && (
          <div
            className="absolute bg-white border-2 border-[#002FA7] shadow-lg p-3 z-20"
            style={{ left: `${newNote.x}%`, top: `${newNote.y}%`, transform: 'translate(-50%, -100%) translateY(-10px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <textarea
              autoFocus
              value={newNote.text}
              onChange={(e) => setNewNote({ ...newNote, text: e.target.value })}
              placeholder="Enter your note..."
              className="w-48 h-20 text-xs border border-[#E5E5E5] p-2 resize-none focus:outline-none focus:border-[#002FA7]"
            />
            <div className="flex gap-2 mt-2">
              <Button size="sm" onClick={saveAnnotation} className="rounded-none text-xs flex-1">
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setNewNote({ x: 0, y: 0, text: "" }); setIsAddingNote(false); }} className="rounded-none text-xs">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Annotations List */}
      {annotations.length > 0 && (
        <div className="p-3 border-t border-[#E5E5E5] bg-[#F9FAFB]">
          <h4 className="text-xs uppercase tracking-wider text-[#4B5563] mb-2">Annotations</h4>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {annotations.map((ann, idx) => (
              <div key={ann.id} className="flex items-start gap-2 text-xs">
                <span className="w-5 h-5 bg-[#002FA7] text-white rounded-full flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                <span className="text-[#0A0A0A] flex-1">{ann.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// PDF Export Function
const exportToPDF = (analysis, annotations = []) => {
  if (!analysis) return;
  
  const dashboard = analysis.dashboard || {};
  const summary = analysis.executive_summary || {};
  
  const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Architecture Analysis Report</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'IBM Plex Mono', monospace; max-width: 900px; margin: 0 auto; padding: 40px; color: #0A0A0A; font-size: 11px; line-height: 1.5; }
    h1 { font-size: 28px; margin-bottom: 10px; border-bottom: 3px solid #0A0A0A; padding-bottom: 10px; }
    h2 { font-size: 18px; margin-top: 30px; margin-bottom: 15px; color: #002FA7; border-bottom: 1px solid #E5E5E5; padding-bottom: 5px; }
    h3 { font-size: 14px; margin-top: 15px; margin-bottom: 10px; }
    p { margin-bottom: 10px; }
    .dashboard { background: #0A0A0A; color: white; padding: 20px; margin-bottom: 30px; }
    .dashboard-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
    .grade { width: 60px; height: 60px; display: flex; align-items: center; justify-content: center; font-size: 32px; font-weight: bold; }
    .grade-A, .grade-B { background: #22c55e; }
    .grade-C { background: #eab308; color: #0A0A0A; }
    .grade-D, .grade-F { background: #ef4444; }
    .metric-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em; color: #9CA3AF; }
    .metric-value { font-size: 14px; font-weight: 600; }
    .risk-critical { background: #ef4444; color: white; padding: 2px 8px; font-size: 10px; text-transform: uppercase; }
    .risk-high { background: #f97316; color: white; padding: 2px 8px; font-size: 10px; text-transform: uppercase; }
    .risk-medium { background: #eab308; color: #0A0A0A; padding: 2px 8px; font-size: 10px; text-transform: uppercase; }
    .risk-low { background: #22c55e; color: white; padding: 2px 8px; font-size: 10px; text-transform: uppercase; }
    .action-item { display: flex; gap: 10px; margin-bottom: 8px; }
    .action-num { width: 20px; height: 20px; background: #002FA7; color: white; display: flex; align-items: center; justify-content: center; font-size: 11px; }
    .section { margin-bottom: 25px; }
    .strengths { border-left: 3px solid #22c55e; padding-left: 15px; margin-bottom: 15px; }
    .risks { background: #fef2f2; border-left: 3px solid #ef4444; padding: 15px; margin-bottom: 15px; }
    .verdict { border: 2px solid #0A0A0A; padding: 15px; background: #F3F4F6; }
    .component { border: 1px solid #E5E5E5; padding: 15px; margin-bottom: 10px; page-break-inside: avoid; }
    .component-critical { border-color: #fca5a5; background: #fef2f2; }
    .component-warning { border-color: #fcd34d; background: #fefce8; }
    .tag { display: inline-block; padding: 2px 6px; font-size: 10px; margin-right: 5px; background: #F3F4F6; }
    .improvement { background: #eff6ff; border-left: 2px solid #3b82f6; padding: 8px; margin-top: 10px; font-size: 10px; }
    .security-section { background: #0A0A0A; color: white; padding: 15px; margin-bottom: 15px; }
    .finding { border: 1px solid #E5E5E5; padding: 12px; margin-bottom: 10px; }
    .finding-critical { border-color: #fca5a5; background: #fef2f2; }
    ul { margin-left: 20px; margin-bottom: 10px; }
    li { margin-bottom: 5px; }
    .annotations { margin-top: 20px; padding-top: 20px; border-top: 2px solid #E5E5E5; }
    .annotation { display: flex; gap: 10px; margin-bottom: 8px; }
    .ann-num { width: 20px; height: 20px; background: #002FA7; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; }
    @media print { body { padding: 20px; } .dashboard { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <h1>Architecture Analysis Report</h1>
  
  <div class="dashboard">
    <div class="dashboard-grid">
      <div style="display: flex; align-items: center; gap: 15px;">
        <div class="grade grade-${dashboard.grade}">${dashboard.grade}</div>
        <div>
          <div style="font-size: 24px; font-weight: bold;">${dashboard.overall_score}/100</div>
          <div class="metric-label">Overall Score</div>
        </div>
      </div>
      <div>
        <div class="metric-label">Confidence</div>
        <div class="metric-value">${dashboard.confidence_label} (${dashboard.confidence_level}%)</div>
        <div class="metric-label" style="margin-top: 10px;">Risk Level</div>
        <div><span class="risk-${dashboard.risk_level}">${dashboard.risk_level?.toUpperCase()}</span></div>
      </div>
      <div>
        <div class="metric-label">Architecture Type</div>
        <div class="metric-value">${dashboard.architecture_type}</div>
        <div style="margin-top: 10px; font-size: 10px; color: #9CA3AF;">${dashboard.architecture_pattern}</div>
      </div>
      <div>
        <div class="metric-label">Top Actions to Improve</div>
        ${(dashboard.top_3_actions || []).map((a, i) => `
          <div class="action-item">
            <span class="action-num">${a.priority}</span>
            <span style="font-size: 10px;">${a.action} <span style="color: #9CA3AF;">(${a.effort})</span></span>
          </div>
        `).join('')}
      </div>
    </div>
  </div>

  <h2>Executive Summary</h2>
  <div class="section">
    <h3>What This Architecture Does</h3>
    <p>${summary.what_it_does || ''}</p>
    
    <div class="strengths">
      <h3 style="color: #16a34a;">What It Does Well</h3>
      <ul>
        ${(summary.what_it_does_well || []).map(s => `<li>✓ ${s}</li>`).join('')}
      </ul>
    </div>
    
    <div class="risks">
      <h3 style="color: #dc2626;">⚠ Key Risks (Critical Attention Required)</h3>
      <ul>
        ${(summary.key_risks || []).map(r => `<li style="color: #991b1b; font-weight: 600;">⚠ ${r}</li>`).join('')}
      </ul>
    </div>
    
    <div class="verdict">
      <h3>Bottom-Line Verdict</h3>
      <p style="font-weight: 600;">${summary.verdict || ''}</p>
    </div>
  </div>

  <h2>System Components (${analysis.components?.length || 0})</h2>
  ${(analysis.components || []).map(c => `
    <div class="component ${c.risk_status === 'critical' ? 'component-critical' : c.risk_status === 'warning' ? 'component-warning' : ''}">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <div>
          <strong>${c.name}</strong>
          <span class="tag">${c.type}</span>
          <span class="tag">${c.layer}</span>
        </div>
        <span class="risk-${c.risk_status}">${c.risk_status?.toUpperCase()}</span>
      </div>
      <p>${c.description}</p>
      <p><strong>Role:</strong> ${c.role_in_system}</p>
      ${c.risk_reason ? `<p style="color: #dc2626;"><strong>Risk:</strong> ${c.risk_reason}</p>` : ''}
      ${c.improvement_hint ? `<div class="improvement"><strong>Improvement:</strong> ${c.improvement_hint}</div>` : ''}
    </div>
  `).join('')}

  <h2>Failure & Resilience Analysis (${analysis.failure_analysis?.length || 0})</h2>
  ${(analysis.failure_analysis || []).map(f => `
    <div class="component">
      <h3>What if ${f.component} fails?</h3>
      <p><strong>What Fails:</strong> ${f.what_fails}</p>
      <p><strong>Immediate Impact:</strong> ${f.immediate_impact}</p>
      <p><strong>Cascading Impact:</strong> ${f.cascading_impact}</p>
      <p style="color: #7c3aed;"><strong>Business Impact:</strong> ${f.business_impact}</p>
      <p><strong>Recovery Time:</strong> ${f.recovery_time}</p>
      <div class="improvement"><strong>Recommendation:</strong> ${f.recommendation}</div>
    </div>
  `).join('')}

  <h2>Security & Compliance</h2>
  <div class="security-section">
    <div style="display: flex; align-items: center; gap: 15px;">
      <div class="grade grade-${analysis.security_review?.security_grade}">${analysis.security_review?.security_grade}</div>
      <div>
        <div style="font-size: 18px; font-weight: bold;">${analysis.security_review?.overall_security_score}/100</div>
        <div class="metric-label">Security Score</div>
      </div>
    </div>
  </div>
  ${(analysis.security_review?.findings || []).map(f => `
    <div class="finding ${f.risk_level === 'critical' || f.risk_level === 'high' ? 'finding-critical' : ''}">
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
        <span class="tag">${f.category}</span>
        <span class="risk-${f.risk_level}">${f.risk_level?.toUpperCase()}</span>
      </div>
      <p><strong>${f.finding}</strong></p>
      <div class="improvement"><strong>Recommendation:</strong> ${f.recommendation}</div>
    </div>
  `).join('')}

  ${annotations.length > 0 ? `
    <div class="annotations">
      <h2>Diagram Annotations</h2>
      ${annotations.map((a, i) => `
        <div class="annotation">
          <span class="ann-num">${i + 1}</span>
          <span>${a.text}</span>
        </div>
      `).join('')}
    </div>
  ` : ''}

  ${analysis.diagram_vs_reality ? `
    <h2 style="margin-top: 30px;">Diagram vs Reality (Coverage: ${analysis.diagram_vs_reality.coverage_score}%)</h2>
    <p style="margin-bottom: 16px; color: #4B5563;">${analysis.diagram_vs_reality.summary || ''}</p>
    ${[
      ['Services Not in Diagram', analysis.diagram_vs_reality.services_not_in_diagram],
      ['Hidden Dependencies', analysis.diagram_vs_reality.hidden_dependencies],
      ['Latency Hotspots', analysis.diagram_vs_reality.latency_hotspots],
      ['Unexpected Call Paths', analysis.diagram_vs_reality.unexpected_paths],
    ].map(([title, items]) => `
      <h3 style="margin-top: 16px; font-size: 13px;">${title} (${items?.length || 0})</h3>
      ${(items || []).map(f => `
        <div class="finding ${f.severity === 'critical' || f.severity === 'high' ? 'finding-critical' : ''}">
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
            <strong>${f.title}</strong>
            <span class="risk-${f.severity}">${(f.severity || '').toUpperCase()}</span>
          </div>
          ${f.detail ? `<p style="margin: 4px 0; color: #4B5563;">${f.detail}</p>` : ''}
          ${f.evidence ? `<code style="display: inline-block; background: #F3F4F6; padding: 2px 6px; font-size: 11px;">${f.evidence}</code>` : ''}
        </div>
      `).join('') || '<p style="color:#9CA3AF; font-style: italic; font-size: 12px;">None.</p>'}
    `).join('')}
  ` : ''}

  <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #E5E5E5; text-align: center; color: #9CA3AF; font-size: 10px;">
    Generated by ArchInsight • ${new Date().toLocaleDateString()}
  </div>
</body>
</html>`;

  const printWindow = window.open("", "_blank");
  printWindow.document.write(html);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 500);
  toast.success("PDF export ready!");
};

// Top Banner Dashboard
const DashboardBanner = ({ dashboard }) => {
  if (!dashboard) return null;
  
  const riskColors = {
    critical: "bg-red-600",
    high: "bg-red-500",
    medium: "bg-yellow-500",
    low: "bg-green-500"
  };
  
  return (
    <div className="bg-[#0A0A0A] text-white p-6 mb-6" data-testid="dashboard-banner">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="flex items-center gap-4">
          <GradeDisplay grade={dashboard.grade} size="large" />
          <div>
            <div className="text-3xl font-black">{dashboard.overall_score}<span className="text-lg font-normal text-gray-400">/100</span></div>
            <div className="text-xs text-gray-400 uppercase tracking-wider">Overall Score</div>
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 uppercase tracking-wider">Confidence</span>
            <span className="text-sm font-semibold">{dashboard.confidence_label} ({dashboard.confidence_level}%)</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 uppercase tracking-wider">Risk Level</span>
            <span className={`px-2 py-0.5 text-xs font-bold uppercase ${riskColors[dashboard.risk_level]} text-white`}>
              {dashboard.risk_level}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400 uppercase tracking-wider">Type</span>
            <span className="text-sm font-semibold">{dashboard.architecture_type}</span>
          </div>
        </div>
        
        <div>
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Architecture Pattern</div>
          <div className="text-sm leading-relaxed">{dashboard.architecture_pattern}</div>
        </div>
        
        <div>
          <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Top 3 Actions to Improve</div>
          <div className="space-y-2">
            {dashboard.top_3_actions?.map((action, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <span className="w-5 h-5 bg-[#002FA7] text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                  {action.priority}
                </span>
                <div className="flex-1">
                  <span className="text-white">{action.action}</span>
                  <span className="text-gray-500 text-xs ml-2">({action.effort} effort)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Executive Summary Section
const ExecutiveSummarySection = ({ summary }) => {
  if (!summary) return null;
  
  return (
    <div className="space-y-4" data-testid="executive-summary-content">
      <div className="border-l-4 border-[#002FA7] pl-4">
        <h4 className="text-xs uppercase tracking-wider text-[#4B5563] mb-2">What This Architecture Does</h4>
        <p className="text-[#0A0A0A] leading-relaxed">{summary.what_it_does}</p>
      </div>
      
      <div className="border-l-4 border-green-500 pl-4">
        <h4 className="text-xs uppercase tracking-wider text-green-700 mb-2">What It Does Well</h4>
        <ul className="space-y-1">
          {summary.what_it_does_well?.map((item, idx) => (
            <li key={idx} className="text-sm text-[#0A0A0A] flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
      </div>
      
      <div className="border-l-4 border-red-500 pl-4 bg-red-50 p-4 -ml-4">
        <h4 className="text-xs uppercase tracking-wider text-red-700 mb-2 flex items-center gap-2">
          <AlertOctagon className="w-4 h-4" />
          Key Risks (Critical Attention Required)
        </h4>
        <ul className="space-y-2">
          {summary.key_risks?.map((risk, idx) => (
            <li key={idx} className="text-sm text-red-900 font-medium flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              {risk}
            </li>
          ))}
        </ul>
      </div>
      
      <div className="border-2 border-[#0A0A0A] p-4 bg-[#F3F4F6]">
        <h4 className="text-xs uppercase tracking-wider text-[#4B5563] mb-2">Bottom-Line Verdict</h4>
        <p className="text-[#0A0A0A] font-semibold leading-relaxed">{summary.verdict}</p>
      </div>
    </div>
  );
};

// Component Breakdown Section
const ComponentsSection = ({ components }) => {
  if (!components || components.length === 0) return <p className="text-sm text-[#4B5563]">No components detected</p>;
  
  return (
    <div className="space-y-3" data-testid="components-content">
      <div className="flex gap-4 mb-4 text-sm">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-red-500"></span>
          {components.filter(c => c.risk_status === 'critical').length} Critical
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-yellow-500"></span>
          {components.filter(c => c.risk_status === 'warning').length} Warning
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-green-500"></span>
          {components.filter(c => c.risk_status === 'healthy').length} Healthy
        </span>
      </div>
      
      {components.map((comp, idx) => (
        <div 
          key={idx} 
          className={`border p-4 transition-colors ${
            comp.risk_status === 'critical' ? 'border-red-300 bg-red-50' :
            comp.risk_status === 'warning' ? 'border-yellow-300 bg-yellow-50' :
            'border-[#E5E5E5] hover:border-[#0A0A0A]'
          }`}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-[#002FA7]" />
              <span className="font-semibold text-[#0A0A0A]">{comp.name}</span>
              <span className="text-xs bg-[#F3F4F6] px-2 py-0.5 text-[#4B5563]">{comp.type}</span>
              <span className="text-xs bg-[#E5E7EB] px-2 py-0.5 text-[#4B5563]">{comp.layer}</span>
            </div>
            <RiskBadge status={comp.risk_status} />
          </div>
          
          <p className="text-sm text-[#4B5563] mb-2">{comp.description}</p>
          
          <div className="text-sm mb-2">
            <span className="text-xs uppercase tracking-wider text-[#002FA7]">Role: </span>
            <span className="text-[#0A0A0A]">{comp.role_in_system}</span>
          </div>
          
          {comp.risk_reason && (
            <div className="text-sm mb-2 text-red-700">
              <span className="text-xs uppercase tracking-wider">Risk Reason: </span>
              {comp.risk_reason}
            </div>
          )}
          
          {comp.improvement_hint && (
            <div className="text-sm bg-blue-50 p-2 border-l-2 border-blue-400">
              <span className="text-xs uppercase tracking-wider text-blue-600">Improvement: </span>
              <span className="text-blue-900">{comp.improvement_hint}</span>
            </div>
          )}
          
          <div className="flex gap-4 mt-3 text-xs text-[#4B5563]">
            {comp.dependencies?.length > 0 && (
              <span>Depends on: {comp.dependencies.join(', ')}</span>
            )}
            {comp.dependents?.length > 0 && (
              <span>Used by: {comp.dependents.join(', ')}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Data Flows Section
const DataFlowsSection = ({ flows }) => {
  if (!flows || flows.length === 0) return <p className="text-sm text-[#4B5563]">No data flows detected</p>;
  
  const dataTypeColors = {
    sensitive: "bg-red-100 text-red-700 border-red-300",
    user_data: "bg-orange-100 text-orange-700 border-orange-300",
    system_data: "bg-blue-100 text-blue-700 border-blue-300",
    public: "bg-green-100 text-green-700 border-green-300"
  };
  
  return (
    <div className="space-y-4" data-testid="data-flows-content">
      {flows.map((flow, idx) => (
        <div key={idx} className="border border-[#E5E5E5] p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-[#0A0A0A] flex items-center gap-2">
              <Workflow className="w-4 h-4 text-[#002FA7]" />
              {flow.flow_name}
            </h4>
            <span className={`text-xs px-2 py-0.5 border ${dataTypeColors[flow.data_type] || dataTypeColors.system_data}`}>
              {flow.data_type?.replace('_', ' ')}
            </span>
          </div>
          
          <p className="text-sm text-[#4B5563] mb-3">{flow.description}</p>
          
          <div className="flex items-center flex-wrap gap-1 mb-3">
            {flow.path?.map((step, i) => (
              <span key={i} className="flex items-center">
                <span className="bg-[#F3F4F6] px-2 py-1 text-xs font-mono">{step}</span>
                {i < flow.path.length - 1 && <ArrowRight className="w-4 h-4 text-[#4B5563] mx-1" />}
              </span>
            ))}
          </div>
          
          {flow.security_concerns?.length > 0 && (
            <div className="bg-red-50 p-2 border-l-2 border-red-400">
              <span className="text-xs uppercase tracking-wider text-red-600">Security Concerns:</span>
              <ul className="mt-1">
                {flow.security_concerns.map((concern, i) => (
                  <li key={i} className="text-xs text-red-700 flex items-start gap-1">
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {concern}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Failure Analysis Section
const FailureAnalysisSection = ({ failures }) => {
  if (!failures || failures.length === 0) return <p className="text-sm text-[#4B5563]">No failure scenarios identified</p>;
  
  return (
    <div className="space-y-4" data-testid="failure-analysis-content">
      {failures.map((failure, idx) => (
        <div key={idx} className="border border-[#E5E5E5] p-4 hover:border-red-300 transition-colors">
          <h4 className="font-semibold text-[#0A0A0A] mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            What if <span className="text-red-600">{failure.component}</span> fails?
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <span className="text-xs uppercase tracking-wider text-[#4B5563]">What Fails</span>
              <p className="text-sm text-[#0A0A0A] mt-1">{failure.what_fails}</p>
            </div>
            <div>
              <span className="text-xs uppercase tracking-wider text-[#4B5563]">Recovery Time</span>
              <p className="text-sm text-[#0A0A0A] mt-1 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {failure.recovery_time}
              </p>
            </div>
          </div>
          
          <div className="space-y-3 mb-4">
            <div className="bg-orange-50 p-3 border-l-2 border-orange-400">
              <span className="text-xs uppercase tracking-wider text-orange-600">Immediate Impact</span>
              <p className="text-sm text-orange-900 mt-1">{failure.immediate_impact}</p>
            </div>
            
            <div className="bg-red-50 p-3 border-l-2 border-red-400">
              <span className="text-xs uppercase tracking-wider text-red-600">Cascading Impact</span>
              <p className="text-sm text-red-900 mt-1">{failure.cascading_impact}</p>
            </div>
            
            <div className="bg-purple-50 p-3 border-l-2 border-purple-400">
              <span className="text-xs uppercase tracking-wider text-purple-600">Business Impact</span>
              <p className="text-sm text-purple-900 mt-1 font-medium">{failure.business_impact}</p>
            </div>
          </div>
          
          {failure.blast_radius?.length > 0 && (
            <div className="mb-4">
              <span className="text-xs uppercase tracking-wider text-[#4B5563]">Blast Radius:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {failure.blast_radius.map((comp, i) => (
                  <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-0.5">{comp}</span>
                ))}
              </div>
            </div>
          )}
          
          <div className="bg-green-50 p-3 border-l-2 border-green-400">
            <span className="text-xs uppercase tracking-wider text-green-600">Recommendation</span>
            <p className="text-sm text-green-900 mt-1">{failure.recommendation}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Bottleneck Analysis Section
const BottleneckSection = ({ bottlenecks }) => {
  if (!bottlenecks || bottlenecks.length === 0) return <p className="text-sm text-[#4B5563]">No bottlenecks detected</p>;
  
  return (
    <div className="space-y-4" data-testid="bottleneck-content">
      {bottlenecks.map((bn, idx) => (
        <div key={idx} className="border border-[#E5E5E5] p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                <Gauge className="w-5 h-5 text-yellow-500" />
                {bn.component}
              </h4>
              <span className="text-xs bg-[#F3F4F6] px-2 py-0.5 text-[#4B5563] mt-1 inline-block">
                {bn.bottleneck_type}
              </span>
            </div>
            <div className="text-right">
              <div className="text-2xl font-black text-[#0A0A0A]">{bn.severity_score}<span className="text-sm font-normal text-[#4B5563]">/10</span></div>
              <RiskBadge status={bn.severity_label} />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <span className="text-xs uppercase tracking-wider text-[#4B5563]">Current State</span>
              <p className="text-sm text-[#0A0A0A] mt-1">{bn.current_state}</p>
            </div>
            <div className="bg-red-50 p-2 border-l-2 border-red-400">
              <span className="text-xs uppercase tracking-wider text-red-600">Breaking Point</span>
              <p className="text-sm text-red-900 mt-1 font-medium">{bn.breaking_point}</p>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-3 border-l-2 border-yellow-400 mb-3">
            <span className="text-xs uppercase tracking-wider text-yellow-600">Threshold Warning</span>
            <p className="text-sm text-yellow-900 mt-1">{bn.threshold_warning}</p>
          </div>
          
          <div className="bg-blue-50 p-3 border-l-2 border-blue-400">
            <span className="text-xs uppercase tracking-wider text-blue-600">Recommendation</span>
            <p className="text-sm text-blue-900 mt-1">{bn.recommendation}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Security Review Section
const SecuritySection = ({ security }) => {
  if (!security) return <p className="text-sm text-[#4B5563]">No security review available</p>;
  
  return (
    <div data-testid="security-content">
      <div className="flex items-center gap-6 mb-6 p-4 bg-[#0A0A0A] text-white">
        <GradeDisplay grade={security.security_grade} size="normal" />
        <div className="flex-1">
          <ScoreBar score={security.overall_security_score} label="Security Score" />
        </div>
      </div>
      
      {security.findings?.length > 0 && (
        <div className="mb-6">
          <h4 className="text-xs uppercase tracking-wider text-[#4B5563] mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Security Findings ({security.findings.length})
          </h4>
          <div className="space-y-3">
            {security.findings.map((finding, idx) => (
              <div key={idx} className={`border p-4 ${
                finding.risk_level === 'critical' ? 'border-red-300 bg-red-50' :
                finding.risk_level === 'high' ? 'border-orange-300 bg-orange-50' :
                'border-[#E5E5E5]'
              }`}>
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider text-[#4B5563] bg-[#E5E7EB] px-2 py-0.5">
                    {finding.category}
                  </span>
                  <RiskBadge status={finding.risk_level} />
                </div>
                <p className="text-sm text-[#0A0A0A] font-medium mb-2">{finding.finding}</p>
                
                {finding.affected_components?.length > 0 && (
                  <div className="mb-2">
                    <span className="text-xs text-[#4B5563]">Affected: </span>
                    {finding.affected_components.map((c, i) => (
                      <span key={i} className="text-xs bg-[#F3F4F6] px-1 py-0.5 mr-1">{c}</span>
                    ))}
                  </div>
                )}
                
                <div className="bg-green-50 p-2 border-l-2 border-green-400">
                  <span className="text-xs uppercase tracking-wider text-green-600">Recommendation:</span>
                  <p className="text-sm text-green-900 mt-1">{finding.recommendation}</p>
                </div>
                
                {finding.compliance_impact && (
                  <div className="mt-2 text-xs text-purple-700 bg-purple-50 p-2">
                    <Lock className="w-3 h-3 inline mr-1" />
                    Compliance: {finding.compliance_impact}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {security.missing_controls?.length > 0 && (
        <div className="mb-6">
          <h4 className="text-xs uppercase tracking-wider text-red-600 mb-2">Missing Security Controls</h4>
          <ul className="space-y-1">
            {security.missing_controls.map((ctrl, idx) => (
              <li key={idx} className="text-sm text-red-700 flex items-start gap-2">
                <X className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {ctrl}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {security.compliance_gaps?.length > 0 && (
        <div>
          <h4 className="text-xs uppercase tracking-wider text-orange-600 mb-2">Compliance Gaps</h4>
          <ul className="space-y-1">
            {security.compliance_gaps.map((gap, idx) => (
              <li key={idx} className="text-sm text-orange-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {gap}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Analysis Results Component
// Diagram vs Reality Section - runtime trace gap analysis
const SEVERITY_STYLES = {
  critical: { bg: "bg-red-50", border: "border-red-500", text: "text-red-800", tag: "bg-red-600 text-white" },
  high:     { bg: "bg-orange-50", border: "border-orange-500", text: "text-orange-800", tag: "bg-orange-500 text-white" },
  medium:   { bg: "bg-yellow-50", border: "border-yellow-500", text: "text-yellow-800", tag: "bg-yellow-500 text-white" },
  low:      { bg: "bg-blue-50", border: "border-blue-400", text: "text-blue-800", tag: "bg-blue-500 text-white" },
  info:     { bg: "bg-gray-50", border: "border-gray-400", text: "text-gray-800", tag: "bg-gray-500 text-white" },
};

const GapFindingCard = ({ finding }) => {
  const s = SEVERITY_STYLES[finding.severity] || SEVERITY_STYLES.info;
  return (
    <div className={`border-l-4 ${s.border} ${s.bg} p-3`} data-testid="gap-finding">
      <div className="flex items-start justify-between gap-3 mb-1">
        <p className={`text-sm font-semibold ${s.text} leading-snug`}>{finding.title}</p>
        <span className={`text-[10px] px-2 py-0.5 ${s.tag} tracking-[0.1em] uppercase flex-shrink-0`}>
          {finding.severity}
        </span>
      </div>
      {finding.detail && <p className="text-xs text-[#4B5563] mb-1">{finding.detail}</p>}
      {finding.evidence && (
        <p className="text-[11px] font-mono text-[#0A0A0A] bg-white/60 inline-block px-2 py-0.5 mt-1">
          {finding.evidence}
        </p>
      )}
    </div>
  );
};

const FindingBucket = ({ icon: Icon, label, items, emptyText, testId }) => (
  <div className="border border-[#E5E5E5] bg-white" data-testid={testId}>
    <div className="flex items-center gap-2 border-b border-[#E5E5E5] px-3 py-2 bg-[#F9FAFB]">
      <Icon className="w-4 h-4 text-[#002FA7]" />
      <span className="text-xs font-semibold tracking-[0.1em] uppercase">{label}</span>
      <span className="ml-auto text-[10px] px-2 py-0.5 bg-[#0A0A0A] text-white">
        {items?.length || 0}
      </span>
    </div>
    <div className="p-3 space-y-2">
      {items && items.length > 0 ? (
        items.map((f, i) => <GapFindingCard key={i} finding={f} />)
      ) : (
        <p className="text-xs text-[#4B5563] italic">{emptyText}</p>
      )}
    </div>
  </div>
);

const DiagramVsRealitySection = ({ dvr }) => {
  if (!dvr) return null;
  const stats = dvr.trace_stats || {};
  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-[#0A0A0A] text-white p-4">
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#9CA3AF] mb-2">Gap Analysis Summary</p>
        <p className="text-sm leading-relaxed">{dvr.summary}</p>
      </div>

      {/* Trace metadata row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 border border-[#E5E5E5] bg-[#F9FAFB] p-3">
        <div>
          <p className="text-[10px] tracking-[0.15em] uppercase text-[#4B5563]">Spans</p>
          <p className="text-lg font-bold font-mono">{stats.total_spans ?? "—"}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.15em] uppercase text-[#4B5563]">Services</p>
          <p className="text-lg font-bold font-mono">{stats.total_services ?? "—"}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.15em] uppercase text-[#4B5563]">Edges</p>
          <p className="text-lg font-bold font-mono">{stats.total_edges ?? "—"}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.15em] uppercase text-[#4B5563]">Root</p>
          <p className="text-xs font-semibold truncate" title={(stats.root_services || []).join(", ")}>
            {(stats.root_services || ["—"]).join(", ")}
          </p>
        </div>
      </div>

      {/* Four finding buckets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FindingBucket
          icon={Server}
          label="Services Not in Diagram"
          items={dvr.services_not_in_diagram}
          emptyText="No unknown services — diagram matches trace service list."
          testId="dvr-services-not-in-diagram"
        />
        <FindingBucket
          icon={GitBranch}
          label="Hidden Dependencies"
          items={dvr.hidden_dependencies}
          emptyText="No hidden edges detected between services."
          testId="dvr-hidden-deps"
        />
        <FindingBucket
          icon={Zap}
          label="Latency Hotspots"
          items={dvr.latency_hotspots}
          emptyText="No high-latency paths flagged."
          testId="dvr-latency-hotspots"
        />
        <FindingBucket
          icon={Workflow}
          label="Unexpected Call Paths"
          items={dvr.unexpected_paths}
          emptyText="Call paths look consistent with a typical topology."
          testId="dvr-unexpected-paths"
        />
      </div>

      {/* Top services/edges from the normalized trace (evidence table) */}
      {(stats.top_services?.length > 0 || stats.top_edges?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {stats.top_services?.length > 0 && (
            <div className="border border-[#E5E5E5] bg-white">
              <div className="border-b border-[#E5E5E5] px-3 py-2 bg-[#F9FAFB]">
                <span className="text-xs font-semibold tracking-[0.1em] uppercase">Top Services by Call Count</span>
              </div>
              <div className="divide-y divide-[#E5E5E5] text-xs font-mono">
                {stats.top_services.slice(0, 6).map((s, i) => (
                  <div key={i} className="flex justify-between px-3 py-1.5">
                    <span className="truncate">{s.name}</span>
                    <span className="text-[#4B5563] flex-shrink-0 ml-2">
                      {s.call_count} calls · p95 {s.p95_ms}ms
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {stats.top_edges?.length > 0 && (
            <div className="border border-[#E5E5E5] bg-white">
              <div className="border-b border-[#E5E5E5] px-3 py-2 bg-[#F9FAFB]">
                <span className="text-xs font-semibold tracking-[0.1em] uppercase">Top Edges by p95 Latency</span>
              </div>
              <div className="divide-y divide-[#E5E5E5] text-xs font-mono">
                {stats.top_edges.slice(0, 6).map((e, i) => (
                  <div key={i} className="flex justify-between px-3 py-1.5">
                    <span className="truncate">{e.from} → {e.to}</span>
                    <span className="text-[#4B5563] flex-shrink-0 ml-2">p95 {e.p95_ms}ms</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AnalysisResults = ({ analysis, annotations = [] }) => {
  if (!analysis) return null;
  const hasDVR = !!analysis.diagram_vs_reality;

  return (
    <div>
      <DashboardBanner dashboard={analysis.dashboard} />
      
      <Accordion type="multiple" defaultValue={hasDVR ? ["diagram-vs-reality", "summary", "components", "security"] : ["summary", "components", "security"]} className="w-full">
        {hasDVR && (
          <AccordionItem value="diagram-vs-reality" className="border-2 border-[#002FA7] mb-4" data-testid="dvr-accordion-item">
            <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F0F4FF] bg-[#F8FAFF]" data-testid="accordion-dvr-trigger">
              <div className="flex items-center gap-3 flex-wrap">
                <Activity className="w-5 h-5 text-[#002FA7]" />
                <span className="text-sm font-semibold tracking-wide uppercase">Diagram vs Reality</span>
                <span className={`ml-2 px-2 py-0.5 text-xs ${
                  analysis.diagram_vs_reality.coverage_score >= 80 ? 'bg-green-100 text-green-700' :
                  analysis.diagram_vs_reality.coverage_score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  Coverage: {analysis.diagram_vs_reality.coverage_score}%
                </span>
                <span className="px-2 py-0.5 text-[10px] bg-[#0A0A0A] text-white tracking-[0.15em] uppercase">
                  {analysis.diagram_vs_reality.trace_stats?.format || 'trace'}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <DiagramVsRealitySection dvr={analysis.diagram_vs_reality} />
            </AccordionContent>
          </AccordionItem>
        )}
        <AccordionItem value="summary" className="border border-[#E5E5E5] mb-4">
          <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F3F4F6]" data-testid="accordion-summary-trigger">
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5 text-[#002FA7]" />
              <span className="text-sm font-semibold tracking-wide uppercase">Executive Summary</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ExecutiveSummarySection summary={analysis.executive_summary} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="components" className="border border-[#E5E5E5] mb-4">
          <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F3F4F6]" data-testid="accordion-components-trigger">
            <div className="flex items-center gap-3">
              <Server className="w-5 h-5 text-[#002FA7]" />
              <span className="text-sm font-semibold tracking-wide uppercase">System Components</span>
              <span className="ml-2 px-2 py-0.5 text-xs bg-[#F3F4F6] text-[#4B5563]">{analysis.components?.length || 0} components</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ComponentsSection components={analysis.components} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="dataflows" className="border border-[#E5E5E5] mb-4">
          <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F3F4F6]" data-testid="accordion-dataflows-trigger">
            <div className="flex items-center gap-3">
              <Workflow className="w-5 h-5 text-[#002FA7]" />
              <span className="text-sm font-semibold tracking-wide uppercase">Data Flows</span>
              <span className="ml-2 px-2 py-0.5 text-xs bg-[#F3F4F6] text-[#4B5563]">{analysis.data_flows?.length || 0} flows</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <DataFlowsSection flows={analysis.data_flows} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="failures" className="border border-[#E5E5E5] mb-4">
          <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F3F4F6]" data-testid="accordion-failures-trigger">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <span className="text-sm font-semibold tracking-wide uppercase">Failure & Resilience Analysis</span>
              <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-700">{analysis.failure_analysis?.length || 0} scenarios</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <FailureAnalysisSection failures={analysis.failure_analysis} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="bottlenecks" className="border border-[#E5E5E5] mb-4">
          <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F3F4F6]" data-testid="accordion-bottlenecks-trigger">
            <div className="flex items-center gap-3">
              <TrendingDown className="w-5 h-5 text-yellow-500" />
              <span className="text-sm font-semibold tracking-wide uppercase">Bottleneck & Performance</span>
              <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700">{analysis.bottleneck_analysis?.length || 0} detected</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <BottleneckSection bottlenecks={analysis.bottleneck_analysis} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="security" className="border border-[#E5E5E5] mb-4">
          <AccordionTrigger className="px-4 py-4 hover:no-underline hover:bg-[#F3F4F6]" data-testid="accordion-security-trigger">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-semibold tracking-wide uppercase">Security & Compliance</span>
              {analysis.security_review && (
                <span className={`ml-2 px-2 py-0.5 text-xs ${
                  analysis.security_review.security_grade === 'A' || analysis.security_review.security_grade === 'B' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  Grade: {analysis.security_review.security_grade}
                </span>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <SecuritySection security={analysis.security_review} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

// About Page
const AboutPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#F3F4F6] font-mono">
      <header className="bg-white border-b border-[#E5E5E5] p-4 lg:p-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-heading text-2xl lg:text-3xl tracking-tighter font-black text-[#0A0A0A] hover:text-[#002FA7] transition-colors">
            ArchInsight
          </Link>
          <Button onClick={() => navigate("/")} variant="outline" className="rounded-none">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to App
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 lg:p-10">
        <div className="bg-white border border-[#E5E5E5] p-8 lg:p-12">
          {/* Hero */}
          <div className="mb-10">
            <h1 className="text-3xl lg:text-4xl font-black text-[#0A0A0A] mb-4">
              👋 About Me
            </h1>
            <p className="text-lg text-[#0A0A0A] leading-relaxed">
              Hi, I'm <strong>Vasanth Natarajan</strong> — a technology leader focused on <strong>Generative AI, Agentic Systems, and Enterprise Architecture</strong>.
            </p>
          </div>

          {/* Why I Built This */}
          <div className="mb-10 border-l-4 border-[#002FA7] pl-6 py-4 bg-[#F9FAFB]">
            <p className="text-lg text-[#4B5563] italic mb-4">
              "Great architecture exists in diagrams—but decisions, risks, and trade-offs stay in people's heads."
            </p>
            <p className="text-[#0A0A0A]">
              I built <strong>ArchInsight</strong> to solve this real problem. This platform uses AI to <strong>analyze, explain, and improve system architectures</strong>, helping engineers, architects, and leaders move from <em>diagrams → decisions</em> faster.
            </p>
          </div>

          {/* What I Focus On */}
          <div className="mb-10">
            <h2 className="text-xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-[#002FA7]" />
              What I Focus On
            </h2>
            <ul className="space-y-2">
              {[
                "Generative AI & Agentic AI systems",
                "Contact Center modernization & intelligent automation",
                "Scalable, resilient enterprise architectures",
                "Translating complex systems into actionable insights"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-[#0A0A0A]">
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Why Architecture Explainer */}
          <div className="mb-10 bg-[#0A0A0A] text-white p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-[#002FA7]" />
              Why ArchInsight?
            </h2>
            <p className="text-gray-300 mb-4">
              Most tools visualize systems. This one helps you <strong className="text-white">understand, evaluate, and evolve them</strong>.
            </p>
            <p className="text-gray-400 mb-2">Think of it as your:</p>
            <ul className="space-y-1 text-white">
              <li>• Architecture reviewer</li>
              <li>• Risk analyzer</li>
              <li>• Optimization advisor</li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="mb-10">
            <h2 className="text-xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#002FA7]" />
              Newsletter
            </h2>
            <p className="text-[#4B5563] mb-4">
              I share practical insights on <strong>AI, architecture, and real-world system design</strong>:
            </p>
            <a
              href="https://vasanthnatarajan.me/newsletter"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-6 py-3 hover:bg-[#001d6e] transition-colors"
            >
              Subscribe to my newsletter
              <ExternalLink className="w-4 h-4" />
            </a>
            <ul className="mt-4 space-y-1 text-sm text-[#4B5563]">
              <li>• Agentic AI patterns</li>
              <li>• Architecture breakdowns</li>
              <li>• Scaling lessons from real systems</li>
              <li>• AI + enterprise strategy</li>
            </ul>
          </div>

          {/* AI Labs */}
          <div className="mb-10">
            <h2 className="text-xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-purple-600" />
              AI Labs
            </h2>
            <p className="text-[#4B5563] mb-4">
              Explore experiments, prototypes, and hands-on builds:
            </p>
            <a
              href="https://vasanthnatarajan.me/ai-labs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-3 hover:bg-purple-700 transition-colors"
            >
              Visit AI Labs
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="mt-4 text-sm text-[#4B5563]">
              This is where I build and test new AI workflows, experiment with LLM + system design, and share early-stage ideas and demos.
            </p>
          </div>

          {/* Let's Connect */}
          <div className="border-t border-[#E5E5E5] pt-8">
            <h2 className="text-xl font-bold text-[#0A0A0A] mb-4">Let's Connect</h2>
            <p className="text-[#4B5563] mb-6">
              If you're working on <strong>AI-driven systems, architecture modernization, or scaling challenges</strong>, I'd love to connect and exchange ideas.
            </p>
            <div className="flex gap-4 flex-wrap">
              <a
                href="https://www.linkedin.com/in/vasan-natarajan"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="linkedin-link"
                className="flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors"
              >
                <Linkedin className="w-5 h-5 text-[#0077B5]" />
                LinkedIn
              </a>
              <a
                href="https://vasanthnatarajan.me/contact"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="website-link"
                className="flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors"
              >
                <Globe className="w-5 h-5 text-[#0A0A0A]" />
                Website
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Shared Analysis View
const SharedAnalysisView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [annotations, setAnnotations] = useState([]);

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await axios.get(`${API}/analysis/${id}`);
        setAnalysis(response.data);
        if (response.data.image_base64 && response.data.mime_type) {
          setImagePreview(`data:${response.data.mime_type};base64,${response.data.image_base64}`);
        }
      } catch (err) {
        setError(err.response?.data?.detail || "Analysis not found");
      } finally {
        setIsLoading(false);
      }
    };
    fetchAnalysis();
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white font-mono flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#002FA7] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white font-mono flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-[#0A0A0A] font-semibold mb-4">{error}</p>
          <Button onClick={() => navigate("/")} className="rounded-none bg-[#002FA7] text-white">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-mono">
      <Toaster position="top-right" />
      
      <header className="bg-white border-b border-[#E5E5E5] p-4 lg:p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <Link to="/" className="font-heading text-2xl lg:text-4xl tracking-tighter font-black text-[#0A0A0A] hover:text-[#002FA7] transition-colors">
              ArchInsight
            </Link>
            <p className="text-[#4B5563] text-xs tracking-[0.2em] uppercase">Shared Analysis</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => exportToPDF(analysis, annotations)} variant="outline" className="rounded-none">
              <FileDown className="w-4 h-4 mr-2" /> Export PDF
            </Button>
            <Button onClick={() => navigate("/")} variant="outline" className="rounded-none">
              <ArrowLeft className="w-4 h-4 mr-2" /> New Analysis
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E5E5E5] sticky top-6">
              <div className="p-3 border-b border-[#E5E5E5]">
                <span className="text-xs tracking-[0.2em] uppercase text-[#4B5563]">Architecture Diagram</span>
              </div>
              <DiagramAnnotator 
                imagePreview={imagePreview} 
                annotations={annotations} 
                setAnnotations={setAnnotations}
                readOnly={false}
              />
            </div>
          </div>
          
          <div className="lg:col-span-2">
            <AnalysisResults analysis={analysis} annotations={annotations} />
          </div>
        </div>
      </main>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [traceJson, setTraceJson] = useState(null); // raw JSON string
  const [traceFileName, setTraceFileName] = useState(null);
  const fileInputRef = useRef(null);
  const traceInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload PNG, JPEG, or WEBP.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(",")[1];
      setImage({ base64, mimeType: file.type, name: file.name });
      setImagePreview(e.target.result);
      setAnalysis(null);
      setAnnotations([]);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files[0]);
  }, [handleFileSelect]);

  const handleTraceFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json")) {
      toast.error("Trace must be a .json file");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Trace file too large (max 25MB). Please sample or trim.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      try {
        JSON.parse(text);
      } catch {
        toast.error("Trace file is not valid JSON");
        return;
      }
      setTraceJson(text);
      setTraceFileName(file.name);
      toast.success(`Trace loaded: ${file.name}`);
    };
    reader.readAsText(file);
  };

  const clearTrace = () => {
    setTraceJson(null);
    setTraceFileName(null);
    if (traceInputRef.current) traceInputRef.current.value = "";
  };

  const handleAnalyze = async () => {
    if (!image) {
      toast.error("Please upload an architecture diagram first.");
      return;
    }
    setIsAnalyzing(true);
    try {
      const payload = {
        image_base64: image.base64,
        mime_type: image.mimeType,
      };
      if (advancedMode && traceJson) {
        payload.trace_json = traceJson;
      }
      const response = await axios.post(`${API}/analyze`, payload);
      setAnalysis(response.data);
      toast.success(
        response.data.diagram_vs_reality
          ? "Analysis complete (with runtime trace)!"
          : "Analysis complete!"
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze diagram");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
    setAnalysis(null);
    setAnnotations([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const shareAnalysis = async () => {
    if (!analysis) return;
    const shareUrl = `${window.location.origin}/share/${analysis.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const exportMarkdown = () => {
    if (!analysis) return;
    let md = `# Architecture Analysis Report\n\n`;
    md += `**Score:** ${analysis.dashboard?.overall_score}/100 (Grade: ${analysis.dashboard?.grade})\n`;
    md += `**Risk Level:** ${analysis.dashboard?.risk_level}\n`;
    md += `**Architecture:** ${analysis.dashboard?.architecture_type}\n\n`;
    
    md += `## Executive Summary\n\n`;
    md += `### What It Does\n${analysis.executive_summary?.what_it_does}\n\n`;
    md += `### Strengths\n`;
    analysis.executive_summary?.what_it_does_well?.forEach(s => md += `- ${s}\n`);
    md += `\n### Key Risks\n`;
    analysis.executive_summary?.key_risks?.forEach(r => md += `- ⚠️ ${r}\n`);
    md += `\n### Verdict\n${analysis.executive_summary?.verdict}\n\n`;
    
    md += `## Components (${analysis.components?.length || 0})\n\n`;
    analysis.components?.forEach(c => {
      md += `### ${c.name} [${c.risk_status?.toUpperCase()}]\n`;
      md += `- **Type:** ${c.type} | **Layer:** ${c.layer}\n`;
      md += `- **Role:** ${c.role_in_system}\n`;
      if (c.improvement_hint) md += `- **Improvement:** ${c.improvement_hint}\n`;
      md += `\n`;
    });

    if (annotations.length > 0) {
      md += `## Annotations\n\n`;
      annotations.forEach((a, i) => md += `${i + 1}. ${a.text}\n`);
    }

    if (analysis.diagram_vs_reality) {
      const dvr = analysis.diagram_vs_reality;
      md += `\n## Diagram vs Reality (Coverage: ${dvr.coverage_score}%)\n\n`;
      md += `${dvr.summary}\n\n`;
      const dumpBucket = (title, items) => {
        md += `### ${title} (${items?.length || 0})\n`;
        if (!items || items.length === 0) { md += `_None._\n\n`; return; }
        items.forEach(f => {
          md += `- **[${f.severity?.toUpperCase()}] ${f.title}**\n  ${f.detail || ""}\n`;
          if (f.evidence) md += `  \`${f.evidence}\`\n`;
        });
        md += `\n`;
      };
      dumpBucket("Services Not in Diagram", dvr.services_not_in_diagram);
      dumpBucket("Hidden Dependencies", dvr.hidden_dependencies);
      dumpBucket("Latency Hotspots", dvr.latency_hotspots);
      dumpBucket("Unexpected Call Paths", dvr.unexpected_paths);
    }

    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "architecture-analysis.md";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Markdown exported!");
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6] font-mono">
      <Toaster position="top-right" />
      
      <header className="bg-white border-b border-[#E5E5E5] p-4 lg:p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl lg:text-5xl tracking-tighter font-black text-[#0A0A0A]" data-testid="app-title">
              ArchInsight
            </h1>
            <p className="text-[#4B5563] text-xs tracking-[0.2em] uppercase mt-1">
              AI-powered architecture analysis to uncover risks, bottlenecks, and improvements
            </p>
          </div>
          <Link 
            to="/about" 
            className="flex items-center gap-2 px-4 py-2 text-sm text-[#4B5563] hover:text-[#0A0A0A] hover:bg-[#F3F4F6] transition-colors"
          >
            <Info className="w-4 h-4" />
            About
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upload Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E5E5E5] sticky top-6">
              <div className="p-3 border-b border-[#E5E5E5] flex items-center justify-between">
                <span className="text-xs tracking-[0.2em] uppercase text-[#4B5563]">Diagram Input</span>
                {image && (
                  <button onClick={clearImage} className="text-[#4B5563] hover:text-[#0A0A0A]">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {!imagePreview ? (
                <div
                  className={`min-h-[250px] flex items-center justify-center transition-colors ${
                    isDragging ? "bg-[#002FA7]/5 border-2 border-dashed border-[#002FA7]" : "bg-[#F9FAFB]"
                  }`}
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                  data-testid="upload-dropzone"
                >
                  <div className="text-center p-6">
                    <UploadCloud className="w-10 h-10 mx-auto text-[#4B5563] mb-3" />
                    <p className="text-[#0A0A0A] font-medium mb-1">Drop your diagram here</p>
                    <p className="text-[#4B5563] text-xs mb-3">PNG, JPEG, or WEBP</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={(e) => handleFileSelect(e.target.files[0])}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="rounded-none text-xs"
                    >
                      Browse Files
                    </Button>
                  </div>
                </div>
              ) : (
                <DiagramAnnotator 
                  imagePreview={imagePreview} 
                  annotations={annotations} 
                  setAnnotations={setAnnotations}
                />
              )}

              <div className="p-3 border-t border-[#E5E5E5] space-y-3">
                <button
                  type="button"
                  onClick={() => setAdvancedMode(!advancedMode)}
                  className="w-full flex items-center justify-between text-xs text-[#4B5563] hover:text-[#0A0A0A] transition-colors"
                  data-testid="advanced-mode-toggle"
                >
                  <span className="flex items-center gap-2">
                    <Beaker className="w-3.5 h-3.5" />
                    <span className="tracking-[0.15em] uppercase">Advanced Mode</span>
                  </span>
                  <span className="text-[10px] font-mono">
                    {advancedMode ? "[ ON ]" : "[ OFF ]"}
                  </span>
                </button>

                {advancedMode && (
                  <div className="border border-dashed border-[#E5E5E5] p-3 space-y-2 bg-[#FAFAFA]" data-testid="advanced-panel">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] tracking-[0.15em] uppercase text-[#4B5563]">
                        Runtime Traces (optional)
                      </p>
                      <span className="text-[10px] text-[#4B5563]">Jaeger / Zipkin JSON</span>
                    </div>
                    <input
                      ref={traceInputRef}
                      type="file"
                      accept="application/json,.json"
                      onChange={(e) => handleTraceFile(e.target.files[0])}
                      className="hidden"
                    />
                    {!traceJson ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => traceInputRef.current?.click()}
                        className="w-full rounded-none text-xs"
                        data-testid="upload-trace-btn"
                      >
                        <UploadCloud className="w-3 h-3 mr-1" /> Upload JSON Trace
                      </Button>
                    ) : (
                      <div className="flex items-center justify-between bg-white border border-[#E5E5E5] px-2 py-1.5 text-xs" data-testid="trace-loaded-row">
                        <span className="flex items-center gap-1.5 truncate">
                          <FileText className="w-3 h-3 text-[#002FA7] flex-shrink-0" />
                          <span className="truncate">{traceFileName}</span>
                        </span>
                        <button
                          onClick={clearTrace}
                          className="text-[#4B5563] hover:text-red-600 ml-2"
                          data-testid="clear-trace-btn"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <p className="text-[10px] text-[#4B5563] leading-relaxed">
                      Adds a <strong>Diagram vs Reality</strong> section comparing your diagram against actual runtime traces.
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleAnalyze}
                  disabled={!image || isAnalyzing}
                  className="w-full rounded-none bg-[#002FA7] hover:bg-[#001d6e] text-white"
                  data-testid="analyze-button"
                >
                  {isAnalyzing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Cpu className="w-4 h-4 mr-2" /> Analyze Architecture</>
                  )}
                </Button>
              </div>

              {analysis && (
                <div className="p-3 border-t border-[#E5E5E5] space-y-2">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={shareAnalysis} className="flex-1 rounded-none text-xs">
                      {copied ? <Check className="w-3 h-3 mr-1" /> : <Share2 className="w-3 h-3 mr-1" />}
                      {copied ? "Copied!" : "Share"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportMarkdown} className="flex-1 rounded-none text-xs">
                      <FileText className="w-3 h-3 mr-1" /> MD
                    </Button>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => exportToPDF(analysis, annotations)} 
                    className="w-full rounded-none text-xs"
                  >
                    <FileDown className="w-3 h-3 mr-1" /> Export PDF
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          {/* Results Panel */}
          <div className="lg:col-span-2">
            {!analysis && !isAnalyzing && (
              <div className="bg-white border border-[#E5E5E5] p-12 text-center">
                <Cpu className="w-16 h-16 mx-auto text-[#E5E7EB] mb-4" />
                <h2 className="text-xl font-semibold text-[#0A0A0A] mb-2">Upload an Architecture Diagram</h2>
                <p className="text-[#4B5563] text-sm max-w-md mx-auto">
                  Get instant analysis with health scores, risk assessment, component breakdown, 
                  failure scenarios, bottleneck detection, and security review.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] bg-[#F9FAFB] text-xs text-[#4B5563]">
              <Activity className="w-3.5 h-3.5 text-[#002FA7]" />
              <span>Want to see a real output first?</span>
            <a href="https://archinsights.vasanthnatarajan.me/share/747708b0-9edc-4b53-b358-4daac2d35829"
               target="_blank"
               rel="noopener noreferrer"
               className="text-[#002FA7] font-semibold hover:underline flex items-center gap-1"
            >
        View live example report <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  </div>
)}

            {isAnalyzing && (
              <div className="bg-white border border-[#E5E5E5] p-12 text-center">
                <Loader2 className="w-12 h-12 mx-auto text-[#002FA7] animate-spin mb-4" />
                <p className="text-[#0A0A0A] font-medium">Analyzing your architecture...</p>
                <p className="text-[#4B5563] text-sm">Identifying components, risks, and recommendations</p>
              </div>
            )}

            {analysis && <AnalysisResults analysis={analysis} annotations={annotations} />}
          </div>
        </div>
      </main>

      <footer className="border-t border-[#E5E5E5] bg-white mt-12 p-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs text-[#4B5563]">
            Built by <Link to="/about" className="text-[#002FA7] hover:underline">Vasanth Natarajan</Link> • Exploring AI, architecture, and real-world systems
          </p>
          <p className="text-xs text-[#4B5563] mt-2">
            <a href="https://vasanthnatarajan.me/newsletter" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">Newsletter</a> • 
            <a href="https://vasanthnatarajan.me/ai-labs" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline ml-1">AI Labs</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/share/:id" element={<SharedAnalysisView />} />
        <Route path="/about" element={<AboutPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
